// Section: Business Address Source (display-only) + lookup buttons (partsTaxLookup gate)
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { MapPin, Search, CheckCircle2, Loader2 } from 'lucide-react';
import { useFeatureFlag } from '@/hooks/useFeatureFlags';
import type { TaxLookupAddress } from '@/lib/parts-financial/taxLookup';
import { isCompleteAddress } from '@/lib/parts-financial/taxLookup';

interface Props {
  address: Partial<TaxLookupAddress>;
  onValidate: () => void;
  onLookup: () => void;
  isLookingUp: boolean;
  lastSourceLabel?: string | null;
  lastLookupAt?: string | null;
}

export function AddressSourceSection({
  address, onValidate, onLookup, isLookingUp, lastSourceLabel, lastLookupAt,
}: Props) {
  const engineEnabled = useFeatureFlag('partsFinancialEngine');
  const lookupEnabled = useFeatureFlag('partsTaxLookup');
  if (!engineEnabled) return null;

  const complete = isCompleteAddress(address);
  const fullAddress = [address.streetAddress, address.city, address.state, address.postalCode, address.country]
    .filter(Boolean).join(', ');

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <MapPin className="h-5 w-5" /> Business Address Source
        </CardTitle>
        <CardDescription>
          Address used for suggested tax lookups. Edit this in the General tab.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="rounded-md border bg-muted/30 px-4 py-3 text-sm">
          {fullAddress || <span className="text-muted-foreground">No address on file. Add one in the General tab.</span>}
        </div>

        {lookupEnabled && (
          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="outline" onClick={onValidate} disabled={!complete || isLookingUp}>
              <CheckCircle2 className="h-4 w-4 mr-2" /> Validate Address
            </Button>
            <Button type="button" onClick={onLookup} disabled={!complete || isLookingUp}>
              {isLookingUp ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Search className="h-4 w-4 mr-2" />}
              Look Up Suggested Tax Settings
            </Button>
            {!complete && (
              <p className="w-full text-xs text-muted-foreground">
                Lookup requires street, city, state, postal code, and country.
              </p>
            )}
          </div>
        )}

        {lastLookupAt && (
          <p className="text-xs text-muted-foreground">
            Last lookup: {new Date(lastLookupAt).toLocaleString()}{' '}
            {lastSourceLabel && <>· source: <span className="font-mono">{lastSourceLabel}</span></>}
          </p>
        )}
      </CardContent>
    </Card>
  );
}
